/**
 * Compact practice-player bar shown at the bottom of the song page (above the safe area) and an
 * expandable sheet with every control. Reads the shared AudioProvider, so it can be shown, hidden
 * or re-mounted without interrupting playback.
 */
import { ChevronUp, Repeat } from 'lucide-react';
import { useState } from 'react';
import { Sheet } from '../components/ui';
import { useT } from '../i18n';
import { AudioErrorLine, AudioFullControls, PlayButton, SeekBar, SkipButton, TrackDot, useRateLabel } from './AudioControls';
import { useAudio, useAudioTime } from './AudioProvider';
import { formatTime, isLoopActive } from './logic';

function TimeLabel() {
  const { time, duration } = useAudioTime();
  return (
    <span className="tabular-nums">
      {formatTime(time)} / {formatTime(duration)}
    </span>
  );
}

export function AudioDock() {
  const t = useT();
  const rateLabel = useRateLabel();
  const { current, rate, loop, error, tracks } = useAudio();
  const [open, setOpen] = useState(false);
  if (tracks.length === 0) return null;
  const loopOn = isLoopActive(loop);

  return (
    <section
      aria-label={t('viewer.audio_player')}
      className="shrink-0 border-t border-[var(--line)] bg-[var(--surface)] shadow-[0_-4px_16px_rgb(16_24_64/0.06)]"
      style={{ paddingBottom: 'var(--safe-bottom)', paddingLeft: 'var(--safe-left)', paddingRight: 'var(--safe-right)' }}
    >
      <div className="mx-auto max-w-5xl px-3 pt-1">
        <SeekBar showTimes={false} className="px-1" />
        <div className="flex items-center gap-1 pb-1.5 sm:gap-2">
          <SkipButton dir={-1} />
          <PlayButton />
          <SkipButton dir={1} />
          {error ? (
            <div className="min-w-0 flex-1 px-1">
              <AudioErrorLine compact />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="flex min-h-12 min-w-0 flex-1 flex-col items-start justify-center rounded-xl px-2 text-left hover:bg-[var(--accent-soft)]"
              aria-label={`${current?.title ?? ''} – ${t('viewer.audio_more')}`}
            >
              <span className="flex w-full min-w-0 items-center gap-2 font-semibold">
                {current ? <TrackDot track={current} /> : null}
                <span className="truncate">{current?.title}</span>
              </span>
              <span className="flex items-center gap-2 text-[14px] text-[var(--muted)]">
                <TimeLabel />
                {rate !== 1 ? <span className="rounded-full bg-[var(--accent-soft)] px-2 font-semibold text-[var(--accent)]">{rateLabel(rate)}</span> : null}
                {loopOn ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--accent-soft)] px-2 font-semibold text-[var(--accent)]">
                    <Repeat className="size-3.5" aria-hidden />
                    A–B
                  </span>
                ) : null}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={t('viewer.audio_more')}
            title={t('viewer.audio_more')}
            className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl text-[var(--ink-2)] hover:bg-[var(--accent-soft)]"
          >
            <ChevronUp className="size-7" aria-hidden />
          </button>
        </div>
      </div>
      <Sheet open={open} onClose={() => setOpen(false)} title={t('viewer.audio_player')} wide>
        <AudioFullControls />
      </Sheet>
    </section>
  );
}
